import {Card, DataStatus} from 'platform/components';
import {Grid, GridItem, HStack, Icon, Show, Text} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {isNil, isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetBusinessCaseCustomerSatisfactionQuery} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, RequiredTestIdProps, parseDate, suffixTestId} from 'shared/web';

import {CustomerSatisfactionCell} from './CustomerSatisfactionCell';
import {CustomerSatisfactionCommentBubble} from './CustomerSatisfactionCommentBubble';
import {CustomerSatisfactionEmptyState} from './CustomerSatisfactionEmptyState';

const STAR_VALUES = [1, 2, 3, 4, 5];

export function CustomerSatisfactionCard(props: {businessCaseId: string} & RequiredTestIdProps) {
  const {data, isLoading, isError} = useGetBusinessCaseCustomerSatisfactionQuery({
    businessCaseId: props.businessCaseId,
  });
  const formatDateTime = useDateTimeFormatter();
  const rating = data?.rating;

  return (
    <Card
      title={i18n.t('page.businessCase.customerSatisfaction.title')}
      data-testid={props['data-testid']}
    >
      <DataStatus isLoading={isLoading} isError={isError}>
        <Show when={rating}>
          {(answer) => (
            <Grid columns={[1, 2, 4]}>
              <CustomerSatisfactionCell
                label={i18n.t('general.labels.rating')}
                data-testid={suffixTestId('rating', props)}
                aria-label={i18n.t('entity.auction.offers.ratingValue', {rating: answer.rating})}
              >
                <HStack spacing={1} align="center">
                  {STAR_VALUES.map((value) => (
                    <Icon
                      key={value}
                      value="toggle/star"
                      size={4}
                      color={
                        value <= answer.rating
                          ? 'palettes.yellow.60.100'
                          : 'palettes.neutral.60.100'
                      }
                    />
                  ))}
                  <Text>
                    {i18n.t('entity.auction.offers.ratingValue', {rating: answer.rating})}
                  </Text>
                </HStack>
              </CustomerSatisfactionCell>
              <CustomerSatisfactionCell
                label={i18n.t('page.businessCase.customerSatisfaction.recommendation')}
                data-testid={suffixTestId('recommendation', props)}
              >
                <Show when={isNotNil(answer.recommendation) ? answer.recommendation : null}>
                  {(recommendation) => (
                    <Text>
                      {i18n.t('page.businessCase.customerSatisfaction.recommendationValue', {
                        recommendation,
                      })}
                    </Text>
                  )}
                </Show>
                <Show when={isNil(answer.recommendation)}>
                  <Text>{EMPTY_PLACEHOLDER}</Text>
                </Show>
              </CustomerSatisfactionCell>
              <Show when={isNotNilOrEmpty(answer.comment) ? answer.comment : null}>
                {(comment) => (
                  <GridItem span={[1, 2, 2]} rowSpan={[1, 1, 2]}>
                    <CustomerSatisfactionCell
                      label={i18n.t('page.comments.labels.comment')}
                      data-testid={suffixTestId('comment', props)}
                    >
                      <CustomerSatisfactionCommentBubble comment={comment} />
                    </CustomerSatisfactionCell>
                  </GridItem>
                )}
              </Show>
              <Show when={data?.checkInSentAt}>
                {(sentAt) => (
                  <CustomerSatisfactionCell
                    label={i18n.t('page.businessCase.customerSatisfaction.checkInSentOn')}
                    data-testid={suffixTestId('checkInSentAt', props)}
                  >
                    <Text>{formatDateTime('dateTimeMedium', parseDate(sentAt))}</Text>
                  </CustomerSatisfactionCell>
                )}
              </Show>
              <CustomerSatisfactionCell
                label={i18n.t('page.businessCase.customerSatisfaction.answeredOn')}
                data-testid={suffixTestId('answeredOn', props)}
              >
                <Text>{formatDateTime('dateTimeMedium', parseDate(answer.submittedAt))}</Text>
              </CustomerSatisfactionCell>
            </Grid>
          )}
        </Show>
        <Show when={isNotNil(data) && isNil(rating) ? data : null}>
          {(satisfaction) => (
            <CustomerSatisfactionEmptyState
              data={satisfaction}
              data-testid={props['data-testid']}
            />
          )}
        </Show>
      </DataStatus>
    </Card>
  );
}
